/**
 * Hero variant 4 — "Paste-up".
 *
 * A flyposted wall: what the firm has on, pasted one over another the way a
 * gig poster goes up over last month's. The top sheet peels away to the next —
 * by itself, or on a tap — and the corners are taped down. Flyposting is how
 * this work actually reaches a street, which is the point the hero makes.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import ElfHeroNav from './ElfHeroNav';
import useMagnetic from './useMagnetic';
import { useConsultOpener } from './elfConsult';
import { scrollPastHero } from './ElfTear';

interface Poster {
  kicker: string;
  title: string;
  line: string;
  foot: string;
}

const POSTERS: Poster[] = [
  {
    kicker: 'Owned property',
    title: 'Made In New Jersey',
    line: 'A statewide arts & culture festival',
    foot: 'All 21 counties · Atlantic City 2027',
  },
  {
    kicker: 'Every month',
    title: 'The Circuit',
    line: 'Networking nights across the launch markets',
    foot: '14 held · running monthly since 2025',
  },
  {
    kicker: 'Merch',
    title: 'Drop 02',
    line: 'Cut for the rooms it belongs to',
    foot: 'Tees · hoodies · registering interest now',
  },
  {
    kicker: 'Marketing',
    title: 'Artist Rollouts',
    line: 'Label-side and independent releases',
    foot: 'Entertainment · Lifestyle · Fashion',
  },
];

const PEEL_MS = 4200;
/** How long a tap holds the wall before it starts peeling again. */
const HOLD_MS = 9000;

export const ElfHeroPosters: React.FC = () => {
  const [at, setAt] = useState(0);
  const heldRef = useRef(false);
  const releaseRef = useRef<number>();
  const magnetic = useMagnetic();
  const bookConsult = useConsultOpener({ source: 'hero.posters', title: 'Book a consultation' });

  useEffect(() => {
    const id = window.setInterval(() => {
      if (heldRef.current) return;
      setAt((i) => (i + 1) % POSTERS.length);
    }, PEEL_MS);
    return () => {
      window.clearInterval(id);
      window.clearTimeout(releaseRef.current);
    };
  }, []);

  const peel = useCallback(() => {
    window.clearTimeout(releaseRef.current);
    heldRef.current = true;
    setAt((i) => (i + 1) % POSTERS.length);
    releaseRef.current = window.setTimeout(() => {
      heldRef.current = false;
    }, HOLD_MS);
  }, []);

  const hold = useCallback((on: boolean) => {
    heldRef.current = on;
  }, []);

  return (
    <div className="elf-hv elf-hv--wall">
      <ElfHeroNav />

      <div className="hvw">
        <div className="hvw__copy">
          <span className="eyebrow an" style={{ alignSelf: 'flex-start', animationDelay: '.2s' }}>
            <i />
            Pasted up, not bought in
          </span>
          <h1 className="hvw__h1">
            <span className="mask">
              <span style={{ animationDelay: '.3s' }}>Culture goes up</span>
            </span>
            <span className="mask">
              <span style={{ animationDelay: '.42s' }}>
                on <em>our wall.</em>
              </span>
            </span>
          </h1>
          <p className="hvw__lede an" style={{ animationDelay: '.66s' }}>
            Marketing, web development with GEO, events, merch and consultations — one roof for
            artists, models and music creators, from a firm that has been inside the culture for
            thirty-five years.
          </p>
          <div className="hvw__cta an" style={{ animationDelay: '.78s' }}>
            <button type="button" className="cta mag" {...magnetic} onClick={bookConsult}>
              Book a Consultation
            </button>
            <button
              type="button"
              className="discover"
              onClick={scrollPastHero}
            >
              Discover <i>↗</i>
            </button>
          </div>
        </div>

        <div
          className="hvw__wall"
          onPointerEnter={(e) => e.pointerType === 'mouse' && hold(true)}
          onPointerLeave={(e) => e.pointerType === 'mouse' && hold(false)}
        >
          {POSTERS.map((p, i) => {
            // Where this sheet sits in the stack right now: 0 is the one pasted
            // on top, and the last place is the sheet peeling off the wall.
            const rel = (i - at + POSTERS.length) % POSTERS.length;
            return (
              <button
                key={p.title}
                type="button"
                className="hvw__poster"
                data-rel={rel}
                tabIndex={rel === 0 ? 0 : -1}
                aria-hidden={rel !== 0}
                aria-label={`${p.title} — show the next poster`}
                onClick={peel}
              >
                <span className="hvw__tape hvw__tape--tl" aria-hidden="true" />
                <span className="hvw__tape hvw__tape--br" aria-hidden="true" />
                <span className="hvw__kicker">{p.kicker}</span>
                <span className="hvw__title">{p.title}</span>
                <span className="hvw__line">{p.line}</span>
                <span className="hvw__rule" aria-hidden="true" />
                <span className="hvw__foot">{p.foot}</span>
                <span className="hvw__brand">ELF-CIRCLE INC · New Jersey</span>
              </button>
            );
          })}
          <span className="hvw__hint">
            <span className="hint-hover">Click</span>
            <span className="hint-touch">Tap</span> to peel
          </span>
        </div>
      </div>
    </div>
  );
};

export default ElfHeroPosters;
